// ============================================================================
// NOTICE TEMPLATE MASTER SCREEN — MSTR-NTMP-001
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useNoticeTemplates } from 'hooks/api/useNoticeTemplates';
import { useCaseTypeOptions } from 'hooks/api/useCaseTypes';
import { noticeTemplateService } from 'api/services/noticeTemplateService';
import { noticeTypeService } from 'api/services/noticeTypeService';
import { actionTypeService } from 'api/services/actionTypeService';
import type { NoticeTemplate, NoticeTemplateSearchRequest } from 'api/types/noticeTemplate.types';
import NoticeTemplateFormModal from 'components/modals/modules/notice-template-module/NoticeTemplateFormModal';
import NoticeTemplatePreviewModal from 'components/modals/modules/notice-template-module/NoticeTemplatePreviewModal';

type ViewMode = 'list' | 'grid';
type SelectOption = { value: string; label: string };

interface NoticeTemplateMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const NoticeTemplateMasterScreen: React.FC<NoticeTemplateMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  // Applicable-category options sourced from mst.tbl_case_type (single source).
  const { options: caseTypeOptions } = useCaseTypeOptions();
  const CATEGORY_FILTER_OPTIONS = useMemo<SelectOption[]>(
    () => [{ value: '', label: 'All categories' }, ...caseTypeOptions],
    [caseTypeOptions],
  );

  const [queryParams, setQueryParams] = useState<NoticeTemplateSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useNoticeTemplates(queryParams);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter state
  const [templateCodeFilter, setTemplateCodeFilter] = useState('');
  const [templateNameFilter, setTemplateNameFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [languageFilter, setLanguageFilter] = useState<string>('');
  const [noticeTypeIdFilter, setNoticeTypeIdFilter] = useState<string>('');
  const [actionTypeIdFilter, setActionTypeIdFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Filter dropdown options
  const [noticeTypeFilterOptions, setNoticeTypeFilterOptions] = useState<SelectOption[]>([]);
  const [actionTypeFilterOptions, setActionTypeFilterOptions] = useState<SelectOption[]>([]);
  const didLoadFilterLookupsRef = useRef(false);

  const loadFilterLookups = useCallback(async () => {
    try {
      const nts: Array<{ id: string; noticeTypeCode: string; noticeTypeName: string }> = [];
      for (let page = 1; page <= 10; page++) {
        const res = await noticeTypeService.getAll({ page, pageSize: 100 });
        nts.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      setNoticeTypeFilterOptions(nts.map(n => ({
        value: n.id,
        label: `${n.noticeTypeName} (${n.noticeTypeCode})`,
      })));
    } catch { setNoticeTypeFilterOptions([]); }

    try {
      const ats: Array<{ id: string; actionCode: string; actionName: string }> = [];
      for (let page = 1; page <= 10; page++) {
        const res = await actionTypeService.getAll({ page, pageSize: 100 });
        ats.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      setActionTypeFilterOptions(ats.map(a => ({
        value: a.id,
        label: `${a.actionName} (${a.actionCode})`,
      })));
    } catch { setActionTypeFilterOptions([]); }
  }, []);

  useEffect(() => {
    if (filterOpen && !didLoadFilterLookupsRef.current) {
      didLoadFilterLookupsRef.current = true;
      loadFilterLookups();
    }
  }, [filterOpen, loadFilterLookups]);

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedTpl, setSelectedTpl] = useState<NoticeTemplate | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(t => t.templateCode === recordCode);
        if (rec) setSelectedTpl(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tplToDelete, setTplToDelete] = useState<NoticeTemplate | null>(null);

  // Preview state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTpl, setPreviewTpl] = useState<NoticeTemplate | null>(null);

  const handlePreview = useCallback((t: NoticeTemplate) => {
    setPreviewTpl(t);
    setPreviewModalOpen(true);
  }, []);
  const handleClosePreview = useCallback(() => {
    setPreviewModalOpen(false);
    setPreviewTpl(null);
  }, []);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedTpl(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback(
    (t: NoticeTemplate) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${t.templateCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedTpl(t);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleView = useCallback(
    (t: NoticeTemplate) => {
      const modalId = tabId ? `${tabId}-view-modal-${t.templateCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedTpl(t);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (t: NoticeTemplate) => {
      setSelectedTpl(t);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((t: NoticeTemplate) => {
    setTplToDelete(t);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!tplToDelete) return;
    setIsDeleting(true);
    try {
      await noticeTemplateService.delete(tplToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Notice Template "${tplToDelete.templateCode}" deleted successfully.`);
      setTplToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [tplToDelete, refetch]);

  const handleSaved = useCallback(() => { refetch(); }, [refetch]);
  const handleRefresh = useCallback(() => { refetch(); }, [refetch]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      templateCode: templateCodeFilter.trim() || undefined,
      templateName: templateNameFilter.trim() || undefined,
      applicableCategory: categoryFilter || undefined,
      languageCode: languageFilter.trim() || undefined,
      noticeTypeId: noticeTypeIdFilter || undefined,
      actionTypeId: actionTypeIdFilter || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [templateCodeFilter, templateNameFilter, categoryFilter, languageFilter, noticeTypeIdFilter, actionTypeIdFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setTemplateCodeFilter('');
    setTemplateNameFilter('');
    setCategoryFilter('');
    setLanguageFilter('');
    setNoticeTypeIdFilter('');
    setActionTypeIdFilter('');
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.templateCode) count++;
    if (queryParams.templateName) count++;
    if (queryParams.applicableCategory) count++;
    if (queryParams.languageCode) count++;
    if (queryParams.noticeTypeId) count++;
    if (queryParams.actionTypeId) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams]);

  const categoryBadgeColor = (cat: string): string => {
    switch (cat) {
      case 'NI_ACT_138':     return '#7c3aed';
      case 'ARBITRATION':    return '#2563eb';
      case 'SARFAESI':       return '#dc2626';
      case 'EP':             return '#c026d3';
      case 'CIVIL_SUIT':     return '#0891b2';
      case 'PRE_LITIGATION': return '#16a34a';
      default:               return '#6b7280';
    }
  };

  const columns: SyutGridColumn<NoticeTemplate>[] = useMemo(() => [
    { id: 'templateCode', header: 'Code', accessorKey: 'templateCode', size: 150, enableSorting: true },
    { id: 'templateName', header: 'Name', accessorKey: 'templateName', size: 300, enableSorting: true, flex: true },
    { id: 'applicableCategory', header: 'Category', accessorKey: 'applicableCategory', size: 150, enableSorting: true,
      cell: ({ row }) => {
        const c = row.original.applicableCategory;
        const color = categoryBadgeColor(c);
        return (
          <span style={{
            fontSize: '0.75rem', fontWeight: 600, color,
            padding: '2px 8px', border: `1px solid ${color}`, borderRadius: '999px',
          }}>
            {c.replace(/_/g, ' ')}
          </span>
        );
      },
    },
    { id: 'noticeTypeCode', header: 'Notice Type', accessorKey: 'noticeTypeCode', size: 160,
      cell: ({ row }) => row.original.noticeTypeCode || '-' },
    { id: 'actionTypeCode', header: 'Action Type', accessorKey: 'actionTypeCode', size: 150,
      cell: ({ row }) => row.original.actionTypeCode || '-' },
    { id: 'languageCode', header: 'Lang', accessorKey: 'languageCode', size: 70 },
    {
      id: 'statusCode', header: 'Status', accessorKey: 'statusCode', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const c = row.original;
        const code = c.deletedAt ? 'DELETED' : (c.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span style={{
            fontSize: '0.75rem', fontWeight: 600, color,
            padding: '2px 8px', border: `1px solid ${color}`, borderRadius: '999px',
          }}>
            {code}
          </span>
        );
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  const getRowActions = useCallback((t: NoticeTemplate): ActionMenuItem[] => [
    { id: 'preview', label: 'Preview', icon: <IconSystem.eye size={16} />, onClick: () => handlePreview(t) },
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(t) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(t) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(t) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(t), variant: 'danger' },
  ], [handlePreview, handleEdit, handleDeleteClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add', tooltip: 'Add New Notice Template', onClick: handleAddNew, variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', tooltip: 'Refresh', onClick: handleRefresh, variant: 'icon' },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Notice Template Master"
        description="Reusable legal-notice body templates (§138 demand, SARFAESI 13(2), arbitration invocation, etc.) with {{placeholder}} tokens."
        icon={<IconSystem.mail size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, subject, notice/action type...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No notice templates found"
              />
            )}

            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.map((t) => (
                    <div
                      key={String(t.id)}
                      style={{
                        position: 'relative',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(t)}
                    >
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(t)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {t.templateCode} · <span style={{ color: categoryBadgeColor(t.applicableCategory) }}>{t.applicableCategory.replace(/_/g, ' ')}</span>
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {t.templateName}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                        {t.noticeTypeName}{t.actionTypeName ? ` · ${t.actionTypeName}` : ''}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: t.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {t.statusCode || 'ACTIVE'} · {t.languageCode}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Template Code</label>
              <input
                type="text"
                className="form-input"
                value={templateCodeFilter}
                onChange={(e) => setTemplateCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Template Name</label>
              <input
                type="text"
                className="form-input"
                value={templateNameFilter}
                onChange={(e) => setTemplateNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Category"
                options={CATEGORY_FILTER_OPTIONS}
                value={categoryFilter}
                onChange={(v) => setCategoryFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Language Code</label>
              <input
                type="text"
                className="form-input"
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value.toUpperCase())}
                placeholder="EN, HI, ..."
                maxLength={10}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Notice Type"
                options={[{ value: '', label: 'All notice types' }, ...noticeTypeFilterOptions]}
                value={noticeTypeIdFilter}
                onChange={(v) => setNoticeTypeIdFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Action Type"
                options={[{ value: '', label: 'All action types' }, ...actionTypeFilterOptions]}
                value={actionTypeIdFilter}
                onChange={(v) => setActionTypeIdFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          tplToDelete
            ? `Are you sure you want to delete notice template "${tplToDelete.templateCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setTplToDelete(null);
          }
        }}
      />

      <NoticeTemplateFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedTpl}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      <NoticeTemplatePreviewModal
        isOpen={previewModalOpen}
        record={previewTpl}
        onClose={handleClosePreview}
      />
    </div>
  );
};

export default NoticeTemplateMasterScreen;
